import { FC, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import BrandDialog from '../../components/BrandDialog';
import { ON_PRIMARY_ERROR_SX } from '../../components/fieldStyles';
import { UserContext } from '../..';
import { BRAND_DIALOG_CLASS } from '../../theme';
import { colors, fontWeights, radii } from '../../theme/tokens';
import { ApiError, GENERIC_ERROR } from './api';
import { SESSION_STORAGE_ARCHIVE_SUCCESS_KEY } from './constants';
import Step1Pages from './Step1Pages';
import Step2Metadata from './Step2Metadata';
import Step3Summary from './Step3Summary';
import WizardStepper from './WizardStepper';
import { metadataValuesFrom, validateMetadata } from './metadata';
import { useDraft } from './useDraft';
import { useMetadataOptions } from './useMetadataOptions';
import {
  EMPTY_METADATA,
  MetadataErrors,
  MetadataValues,
  WizardStep,
} from './types';

// The "Ajouter un document" modal (XD screens 3, 4 and 5), with
// multi-page (ROADMAP.md, Part 3). It opens over the Home when navigating to
// `/archive`; closing it returns to `/my-documents`.
//
// On entry, if there are own drafts it offers to resume the most recent or
// start another; if not, the draft is created and it starts at step 1. The
// three steps: page collector → data and page navigator →
// summary and assembly. Step 2's "Suivant" saves the metadata
// (`PATCH /archives/`); step 3's "Valider" assembles the PDF, which is what
// archives the draft.
//
// Closing (✕ or "Annuler") asks before discarding, and discarding deletes the
// draft with its pages. A just-created draft, with no pages nor data,
// is deleted without asking: there is nothing to lose.
//
// Mobile (ROADMAP.md, Part 2): BrandDialog turns it into a full-screen
// sheet with the primary action pinned at the bottom; the stepper only labels
// the active step; the preview is a thumbnail that opens the viewer.

type Props = {
  open: boolean;
  onClose: () => void;
};

const formatStarted = (iso?: string): string => {
  if (!iso) return '';
  const date = dayjs(iso);
  return date.isValid() ? date.locale('fr').format('DD/MM/YYYY à HH:mm') : '';
};

const ArchiveWizard: FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const userId = userContext?.mainUser?.id;

  const draft = useDraft(userId, open);
  const [step, setStep] = useState<WizardStep>(1);
  const [values, setValues] = useState<MetadataValues>(EMPTY_METADATA);
  const [errors, setErrors] = useState<MetadataErrors>({});
  const [formError, setFormError] = useState<string>();
  const [pageIndex, setPageIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assembling, setAssembling] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const { serviceOptions, issuerOptions, serviceName, issuerName } =
    useMetadataOptions(values.service);

  // When loading (or resuming) a draft, the form starts from what it already had.
  const documentId = draft.document?.id;
  useEffect(() => {
    if (draft.document) {
      setValues(metadataValuesFrom(draft.document));
      setErrors({});
      setFormError(undefined);
      setStep(1);
      setPageIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  // The displayed page cannot point beyond the last one.
  useEffect(() => {
    if (pageIndex > 0 && pageIndex >= draft.pages.length) {
      setPageIndex(Math.max(draft.pages.length - 1, 0));
    }
  }, [draft.pages.length, pageIndex]);

  const update = (patch: Partial<MetadataValues>) => {
    setValues((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      (Object.keys(patch) as Array<keyof MetadataValues>).forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const busy = saving || assembling || discarding;
  const ready = draft.phase.name === 'ready';

  const hasSomethingToLose =
    draft.rows.length > 0 ||
    values.title.trim() !== '' ||
    values.issuingDate !== null ||
    values.service !== '' ||
    values.issuer.trim() !== '' ||
    values.confidential;

  // --- Navigation between steps -----------------------------------------

  const canLeaveStep1 =
    draft.pages.length > 0 && !draft.uploadsInProgress && !draft.busy;

  // Pages carrying different codes may well be one document (the client's carry
  // a code per page), or a page slipped in from another one. The code cannot
  // tell, so the user is asked before archiving, and asked again whenever the
  // pages change.
  const codeCount = draft.qr.status === 'mismatch' ? draft.qr.urls.length : 0;
  const [pagesConfirmed, setPagesConfirmed] = useState(false);
  const mustConfirmPages = codeCount > 1 && !pagesConfirmed;

  useEffect(() => {
    setPagesConfirmed(false);
  }, [draft.pages]);

  const goToStep3 = async () => {
    const found = validateMetadata(values);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    setSaving(true);
    setFormError(undefined);
    try {
      await draft.saveMetadata({
        title: values.title.trim(),
        issuing_date: (values.issuingDate as dayjs.Dayjs).format('YYYY-MM-DD'),
        service: values.service,
        issuer: values.issuer.trim(),
        confidential: values.confidential,
        verification_url: draft.qr.status === 'consistent' ? draft.qr.url : '',
      });
      setStep(3);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : GENERIC_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const validateAndArchive = async () => {
    setAssembling(true);
    setFormError(undefined);
    try {
      await draft.assemble();
      sessionStorage.setItem(SESSION_STORAGE_ARCHIVE_SUCCESS_KEY, 'true');
      navigate('/my-documents');
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : GENERIC_ERROR);
      setAssembling(false);
    }
  };

  // --- Closing and discarding -------------------------------------------

  const discardAndLeave = async () => {
    setDiscarding(true);
    try {
      await draft.discard();
    } catch (_) {
      // The draft remains for a "Reprendre" or for the backend's purge;
      // the user is not held back because of it.
    } finally {
      setDiscarding(false);
      setConfirmOpen(false);
      onClose();
    }
  };

  const requestClose = () => {
    if (busy) return;
    // Before there is a draft (choice, loading, error) nothing has been created.
    if (!draft.document) {
      onClose();
      return;
    }
    if (!hasSomethingToLose) {
      discardAndLeave();
      return;
    }
    setConfirmOpen(true);
  };

  // --- Per-step actions -------------------------------------------------

  const cancelButton = (
    <Button variant="outlined" color="inherit" onClick={requestClose} disabled={busy}>
      Annuler
    </Button>
  );

  const actions = !ready ? (
    cancelButton
  ) : step === 1 ? (
    <>
      {cancelButton}
      <Button
        variant="contained"
        color="secondary"
        onClick={() => setStep(2)}
        disabled={!canLeaveStep1 || busy}
      >
        Suivant
      </Button>
    </>
  ) : step === 2 ? (
    <>
      <Button
        variant="outlined"
        color="inherit"
        onClick={() => setStep(1)}
        disabled={busy}
      >
        Précédent
      </Button>
      <Button
        variant="contained"
        color="secondary"
        onClick={goToStep3}
        disabled={busy}
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        Suivant
      </Button>
    </>
  ) : (
    <>
      {cancelButton}
      <Button
        variant="outlined"
        color="inherit"
        onClick={() => setStep(2)}
        disabled={busy}
      >
        Précédent
      </Button>
      <Button
        variant="contained"
        color="secondary"
        onClick={validateAndArchive}
        disabled={busy || draft.pages.length === 0 || mustConfirmPages}
        startIcon={assembling ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        Valider
      </Button>
    </>
  );

  // --- Body -------------------------------------------------------------

  const renderPhase = () => {
    const phase = draft.phase;

    if (phase.name === 'checking' || phase.name === 'loading') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="inherit" />
        </Box>
      );
    }

    if (phase.name === 'error') {
      return (
        <Typography role="alert" sx={{ py: 4, textAlign: 'center' }}>
          {phase.message}
        </Typography>
      );
    }

    if (phase.name === 'choose') {
      const latest = phase.drafts[0];
      const started = formatStarted(latest.updatedAt || latest.createdAt);
      return (
        <Box
          data-testid="resume-draft"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
            py: 4,
          }}
        >
          <Typography sx={{ fontWeight: fontWeights.semiBold, fontSize: '1.125rem' }}>
            Un document est en cours d'ajout
          </Typography>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderRadius: `${radii.card}px`,
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            }}
          >
            <Typography sx={{ fontWeight: fontWeights.medium }}>
              {latest.title?.trim() || 'Brouillon sans titre'}
            </Typography>
            {started && (
              <Typography sx={{ fontSize: '0.8125rem', opacity: 0.85 }}>
                Dernière modification le {started}
              </Typography>
            )}
            {phase.drafts.length > 1 && (
              <Typography sx={{ fontSize: '0.8125rem', opacity: 0.85, mt: 0.5 }}>
                {phase.drafts.length - 1} autre{phase.drafts.length > 2 ? 's' : ''} brouillon
                {phase.drafts.length > 2 ? 's' : ''} en attente.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button variant="outlined" color="inherit" onClick={() => draft.startNew()}>
              Nouveau document
            </Button>
            <Button variant="contained" color="secondary" onClick={() => draft.resume(latest)}>
              Reprendre
            </Button>
          </Box>
        </Box>
      );
    }

    if (step === 1) {
      return (
        <Step1Pages
          rows={draft.rows}
          busy={draft.busy}
          uploadsInProgress={draft.uploadsInProgress}
          onAddFiles={draft.addFiles}
          onMove={draft.movePage}
          onRemove={draft.removePage}
          onRetry={draft.retryUpload}
          onDismiss={draft.dismissUpload}
        />
      );
    }

    if (step === 2) {
      return (
        <Step2Metadata
          values={values}
          errors={errors}
          onChange={update}
          serviceOptions={serviceOptions}
          issuerOptions={issuerOptions}
          pages={draft.pages}
          qr={draft.qr}
          pageIndex={pageIndex}
          onPageIndexChange={setPageIndex}
          disabled={saving}
        />
      );
    }

    return (
      <>
        {codeCount > 1 && (
          <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                checked={pagesConfirmed}
                onChange={(e) => setPagesConfirmed(e.target.checked)}
                disabled={assembling}
                sx={{ color: colors.white }}
              />
            }
            label={`Les pages portent ${codeCount} QR codes différents. Je confirme qu'elles appartiennent toutes au même document.`}
            sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', pt: 1.1 } }}
          />
        )}
        <Step3Summary
        values={values}
        serviceName={serviceName(values.service)}
        issuerName={issuerName(values.issuer)}
        pages={draft.pages}
        qr={draft.qr}
        pageIndex={pageIndex}
        onPageIndexChange={setPageIndex}
        assembling={assembling}
      />
      </>
    );
  };

  return (
    <>
      <BrandDialog
        open={open}
        title="Ajouter un document"
        onClose={requestClose}
        busy={busy}
        maxWidth="lg"
        actions={actions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {ready && <WizardStepper active={step} />}

          {step === 1 && codeCount > 1 && ready && (
            <Typography role="status" sx={{ fontSize: '0.875rem' }}>
              Les pages portent {codeCount} QR codes différents. Vous devrez
              confirmer qu'elles appartiennent toutes au même document avant de
              l'archiver.
            </Typography>
          )}

          {formError && (
            <Typography role="alert" sx={{ ...ON_PRIMARY_ERROR_SX, fontSize: '0.875rem' }}>
              {formError}
            </Typography>
          )}

          {/* Page notices (rejected file, failed reordering…):
              inside the modal, which is where one looks. A Snackbar would sit
              under the backdrop: it does not use a portal and the shell traps it
              in its stacking context. */}
          {draft.notice && (
            <Alert
              severity="error"
              variant="filled"
              role="alert"
              onClose={draft.clearNotice}
              sx={{
                whiteSpace: 'pre-line',
                backgroundColor: colors.accent,
                color: colors.white,
                '& .MuiAlert-icon, & .MuiAlert-action': { color: colors.white },
              }}
            >
              {draft.notice}
            </Alert>
          )}

          {renderPhase()}
        </Box>
      </BrandDialog>

      {/* Discard confirmation, with the XD modal pattern */}
      <Dialog
        className={BRAND_DIALOG_CLASS}
        open={confirmOpen}
        onClose={() => !discarding && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Abandonner le document ?</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Les pages déjà chargées et les informations saisies seront
            supprimées. Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setConfirmOpen(false)}
            disabled={discarding}
          >
            Continuer la saisie
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={discardAndLeave}
            disabled={discarding}
            startIcon={discarding ? <CircularProgress size={16} color="inherit" /> : undefined}
            autoFocus
          >
            Abandonner
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ArchiveWizard;
